import React from 'react';
import {
    Box, Grid, Card, CardContent, Typography, Button, Stack, Chip, Divider, IconButton, Tooltip
} from '@mui/material';
import {
    GridView as HubIcon,
    FactCheck as AttendanceIcon,
    School as SchoolCertIcon,
    Badge as RegFormIcon,
    Groups as StudentsListIcon,
    ContactPage as InfoIcon,
    Badge as CardsIcon,
    TransferWithinAStation as CancellationIcon,
    Verified as ConductIcon,
    Mail as ConvocationIcon,
    LocalHospital as EmergencyIcon,
    CalendarMonth as PlanningIcon,
    Description as InvoicesIcon,
    Receipt as ReceiptsIcon,
    AccountBalance as DailyCashIcon,
    ArrowForward as OpenIcon,
    Print as PrintIcon
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import GenericPage from '../../Commun/components/GenericPage';
import { useLanguage } from '../../Commun/contexts/LanguageContext';

interface EditionCardItem {
    id: string;
    title: string;
    titleAr: string;
    titleEn: string;
    subtitleAr: string;
    descriptionFr: string;
    descriptionAr: string;
    descriptionEn: string;
    route: string;
    icon: React.ReactNode;
    category: string;
    categoryAr: string;
    categoryEn: string;
}

export const EditionsHubPage: React.FC = () => {
    const { language, t } = useLanguage();
    const navigate = useNavigate();
    const isRtl = language === 'ar';

    const cards: EditionCardItem[] = [
        {
            id: 'attendance',
            title: 'Liste de classe / feuille de présence',
            titleAr: 'قائمة القسم / ورقة الحضور',
            titleEn: 'Class List / Attendance Sheet',
            subtitleAr: 'قائمة القسم / ورقة الحضور',
            descriptionFr: 'Les apprenants d’une classe, une colonne par jour, une case à cocher — pour l’appel.',
            descriptionAr: 'تلاميذ القسم مع خانة لكل يوم للتأشير والملاحظات لمتابعة الحضور والغياب اليومي.',
            descriptionEn: 'Students of a class, one column per day, checkable boxes for daily roll call.',
            route: '/Academy/print-attendance-sheet',
            icon: <AttendanceIcon sx={{ fontSize: 36, color: 'primary.main' }} />,
            category: 'Listes & Appels',
            categoryAr: 'القوائم والحضور',
            categoryEn: 'Lists & Attendance'
        },
        {
            id: 'school_cert',
            title: 'Certificat de scolarité',
            titleAr: 'شهادة مدرسية',
            titleEn: 'School Enrollment Certificate',
            subtitleAr: 'شهادة مدرسية',
            descriptionFr: 'L’attestation que l’apprenant est bien inscrit cette année, pour l’administration ou une autre école.',
            descriptionAr: 'شهادة رسمية تثبت تسجيل التلميذ بصفة نظامية خلال السنة الدراسية الجارية للإدارة أو الملفات الرسمية.',
            descriptionEn: 'Official certification confirming student enrollment for the current academic year.',
            route: '/Academy/print-school-certificate',
            icon: <SchoolCertIcon sx={{ fontSize: 36, color: 'info.main' }} />,
            category: 'Documents & Certificats',
            categoryAr: 'الوثائق والشهادات',
            categoryEn: 'Documents & Certificates'
        },
        {
            id: 'reg_form',
            title: "Fiche d’inscription de l’apprenant",
            titleAr: 'بطاقة تسجيل التلميذ',
            titleEn: 'Student Registration Form',
            subtitleAr: 'بطاقة تسجيل التلميذ',
            descriptionFr: 'Identité, classe, parent, adresse, pièces du dossier et mensualité — la fiche complète, à signer.',
            descriptionAr: 'الهوية، القسم، الولي، العنوان، وثائق الملف والاشتراك الشهري — استمارة شاملة وجاهزة للتوقيع.',
            descriptionEn: 'Identity, class, parents, address, required checklist documents and tuition fees to sign.',
            route: '/Academy/print-registration-form',
            icon: <RegFormIcon sx={{ fontSize: 36, color: 'success.main' }} />,
            category: 'Documents & Certificats',
            categoryAr: 'الوثائق والشهادات',
            categoryEn: 'Documents & Certificates'
        },
        {
            id: 'students_list',
            title: 'Liste des apprenants',
            titleAr: 'قائمة التلاميذ',
            titleEn: 'Students Roster',
            subtitleAr: 'قائمة التلاميذ',
            descriptionFr: 'Tous les apprenants inscrits avec leur classe et le téléphone du parent — pour le bureau ou l’administration.',
            descriptionAr: 'جميع التلاميذ المسجلين حسب القسم مع رقم هاتف الولي للاستعمال الإداري والإحصائي.',
            descriptionEn: 'All registered students with their respective classes and guardian contact phone numbers.',
            route: '/Academy/print-students-list',
            icon: <StudentsListIcon sx={{ fontSize: 36, color: 'secondary.main' }} />,
            category: 'Listes & Appels',
            categoryAr: 'القوائم والحضور',
            categoryEn: 'Lists & Attendance'
        },
        {
            id: 'info_sheet',
            title: 'Fiche de renseignements (vierge)',
            titleAr: 'استمارة معلومات (فارغة)',
            titleEn: 'Information Sheet (Blank)',
            subtitleAr: 'استمارة معلومات (فارغة)',
            descriptionFr: 'À donner aux parents à l’inscription : identité, contacts, santé, personnes autorisées.',
            descriptionAr: 'استمارة تسلم للأولياء عند التسجيل لجمع معلومات الهوية، الصحة، جهات الاتصال والأشخاص المخولين.',
            descriptionEn: 'Distributed to parents upon registration: identity, health details, contacts, authorized guardians.',
            route: '/Academy/print-information-sheet',
            icon: <InfoIcon sx={{ fontSize: 36, color: 'warning.main' }} />,
            category: 'Documents & Certificats',
            categoryAr: 'الوثائق والشهادات',
            categoryEn: 'Documents & Certificates'
        },
        {
            id: 'cards',
            title: "Badge d'apprenant",
            titleAr: 'شارة التلميذ',
            titleEn: 'Student Badge',
            subtitleAr: 'شارة التلميذ',
            descriptionFr: 'La carte que l’apprenant garde sur lui : nom, classe, numéro et QR. Une seule, ou une planche de huit à découper pour toute la classe.',
            descriptionAr: 'بطاقة التلميذ الرسمية: الاسم، القسم، الرقم ورمز QR. طباعة فردية أو بطاقات مجمعة لكل القسم للقص.',
            descriptionEn: 'Official student badge with photo, class, code, and QR Code. Print single or batch cards.',
            route: '/Academy/print-student-cards',
            icon: <CardsIcon sx={{ fontSize: 36, color: 'success.dark' }} />,
            category: 'Listes & Appels',
            categoryAr: 'القوائم والحضور',
            categoryEn: 'Lists & Attendance'
        },
        {
            id: 'cancellation',
            title: 'Certificat de radiation (transfert)',
            titleAr: 'شهادة الشطب (التحويل)',
            titleEn: 'Withdrawal / Transfer Certificate',
            subtitleAr: 'شهادة الشطب',
            descriptionFr: 'Le document qu’exige l’école d’accueil : l’apprenant, sa classe, la date de radiation et le motif.',
            descriptionAr: 'الوثيقة الرسمية المطلوبة لمؤسسة الاستقبال: التلميذ، قسمه، تاريخ الشطب والسبب مع تبرئة الذمة المالية.',
            descriptionEn: 'Official certificate required by host school: student, class, withdrawal date, and reason.',
            route: '/Academy/print-cancellation-certificate',
            icon: <CancellationIcon sx={{ fontSize: 36, color: 'error.main' }} />,
            category: 'Documents & Certificats',
            categoryAr: 'الوثائق والشهادات',
            categoryEn: 'Documents & Certificates'
        },
        {
            id: 'conduct',
            title: 'Certificat de bonne conduite',
            titleAr: 'شهادة حسن السيرة والسلوك',
            titleEn: 'Certificate of Good Conduct',
            subtitleAr: 'شهادة حسن السيرة والسلوك',
            descriptionFr: 'Réclamé pour un concours, une bourse ou une inscription à l’étranger. Il atteste la période de scolarisation.',
            descriptionAr: 'مطلوبة للمسابقات، المنح أو الدراسة بالخارج، تشهد بالسيرة الحسنة والانضباط خلال فترة التمدرس بالمؤسسة.',
            descriptionEn: 'Requested for exams, scholarships or foreign admission. Certifies exemplary discipline and schooling.',
            route: '/Academy/print-conduct-certificate',
            icon: <ConductIcon sx={{ fontSize: 36, color: 'info.dark' }} />,
            category: 'Documents & Certificats',
            categoryAr: 'الوثائق والشهادات',
            categoryEn: 'Documents & Certificates'
        },
        {
            id: 'convocation',
            title: 'Convocation du parent',
            titleAr: 'استدعاء ولي التلميذ',
            titleEn: 'Parent Meeting Summons',
            subtitleAr: 'استدعاء ولي التلميذ',
            descriptionFr: 'Le mot officiel qu’on remet à l’apprenant : date, heure, motif, et le talon à retourner signé.',
            descriptionAr: 'استدعاء رسمي يسلم للتلميذ لولي أمره: التاريخ، التوقيت، السبب، مع قسيمة قابلة للقص والإرجاع موقعة.',
            descriptionEn: 'Official summons handed to student: appointment date, time, reason, and tear-off return receipt.',
            route: '/Academy/print-parent-convocation',
            icon: <ConvocationIcon sx={{ fontSize: 36, color: 'primary.dark' }} />,
            category: 'Correspondance & Médical',
            categoryAr: 'المراسلات والصحة',
            categoryEn: 'Correspondence & Health'
        },
        {
            id: 'emergency',
            title: 'Fiche d’urgence et autorisation de soins',
            titleAr: 'بطاقة الطوارئ والترخيص بالعلاج',
            titleEn: 'Emergency Form & Care Authorization',
            subtitleAr: 'بطاقة الطوارئ والترخيص بالعلاج',
            descriptionFr: 'Qui appeler, quel groupe sanguin, quelles allergies — et l’autorisation écrite du parent pour les soins urgents.',
            descriptionAr: 'أرقام الطوارئ، الزمرة الدموية، الحساسية والأمراض المزمنة مع الترخيص الكتابي للولي لتقديم الإسعافات.',
            descriptionEn: 'Who to call, blood group, allergies, chronic illness, and parental authorization for urgent care.',
            route: '/Academy/print-emergency-form',
            icon: <EmergencyIcon sx={{ fontSize: 36, color: 'error.dark' }} />,
            category: 'Correspondance & Médical',
            categoryAr: 'المراسلات والصحة',
            categoryEn: 'Correspondence & Health'
        },
        // Existing Finance & Planning editions
        {
            id: 'planning',
            title: 'Prints Planning',
            titleAr: 'طباعة التخطيط',
            titleEn: 'Print Planning',
            subtitleAr: 'جدول الحصص والتخطيط',
            descriptionFr: 'Planning complet des séances de formation par salle, enseignant et groupe.',
            descriptionAr: 'الجدول الكامل للحصص التدريبية والدروس حسب القاعات، الأساتذة والأفواج.',
            descriptionEn: 'Complete scheduling agenda for academy sessions grouped by room, teacher, and group.',
            route: '/Academy/print-planning',
            icon: <PlanningIcon sx={{ fontSize: 36, color: 'secondary.dark' }} />,
            category: 'Finances & Planning',
            categoryAr: 'المالية والتخطيط',
            categoryEn: 'Finance & Planning'
        },
        {
            id: 'invoices',
            title: 'Factures',
            titleAr: 'الفواتير',
            titleEn: 'Invoices Report',
            subtitleAr: 'فواتير التسجيل والدراسة',
            descriptionFr: 'Rapport complet des factures émises, montants réglés et soldes d’écolage restants.',
            descriptionAr: 'تقرير شامل للفواتير الصادرة والمبالغ المحصلة مع رصيد المستحقات المتبقية.',
            descriptionEn: 'Full report of issued tuition invoices, settled payments, and outstanding balances.',
            route: '/Academy/print-invoices',
            icon: <InvoicesIcon sx={{ fontSize: 36, color: 'info.main' }} />,
            category: 'Finances & Planning',
            categoryAr: 'المالية والتخطيط',
            categoryEn: 'Finance & Planning'
        },
        {
            id: 'receipts',
            title: 'Reçus de Paiement',
            titleAr: 'إيصالات الدفع',
            titleEn: 'Payment Receipts',
            subtitleAr: 'سندات وإيصالات التحصيل',
            descriptionFr: 'Journal des encaissements et reçus de paiement ventilés par mode de règlement.',
            descriptionAr: 'سجل المقبوضات وإيصالات التحصيل مصنفة حسب طريقة الدفع (نقداً، بطاقة، تحويل).',
            descriptionEn: 'Log of received payments and receipts broken down by payment method.',
            route: '/Academy/print-receipts',
            icon: <ReceiptsIcon sx={{ fontSize: 36, color: 'success.main' }} />,
            category: 'Finances & Planning',
            categoryAr: 'المالية والتخطيط',
            categoryEn: 'Finance & Planning'
        },
        {
            id: 'daily_cash',
            title: 'Rapport de Caisse Journalier',
            titleAr: 'تقرير الصندوق اليومي',
            titleEn: 'Daily Cash Report',
            subtitleAr: 'حركة الصندوق اليومية',
            descriptionFr: 'Synthèse des recettes du jour avec récapitulatif caisse espèces, carte bancaire et virement.',
            descriptionAr: 'ملخص يومي لحركة الصندوق وتوزيع المقبوضات بين النقد، البطاقة والتحويل البنكي.',
            descriptionEn: 'Daily cash reconciliation and breakdown of daily revenues across payment methods.',
            route: '/Academy/print-daily-cash',
            icon: <DailyCashIcon sx={{ fontSize: 36, color: 'warning.dark' }} />,
            category: 'Finances & Planning',
            categoryAr: 'المالية والتخطيط',
            categoryEn: 'Finance & Planning'
        }
    ];

    return (
        <GenericPage
            title={language === 'ar' ? 'مركز الطباعة والإصدارات' : language === 'en' ? 'Print Editions Center' : "Centre d'éditions et impressions"}
            icon={<HubIcon />}
            type="crud"
            footerEnabled={false}
        >
            <Box sx={{ p: 1 }}>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                    {language === 'ar'
                        ? 'اختر الاستمارة أو التقرير المطلوب لتخصيص خيارات التصفية وتوليد المستندات بصيغ PDF أو Excel أو Word.'
                        : language === 'en'
                        ? 'Select an edition template below to customize filters, preview documents, and export to PDF, Excel, or Word.'
                        : 'Sélectionnez un modèle d’édition ci-dessous pour appliquer vos filtres, prévisualiser le document et l’exporter en PDF, Excel ou Word.'}
                </Typography>

                <Grid container spacing={3}>
                    {cards.map(card => {
                        const title = language === 'ar' ? card.titleAr : language === 'en' ? card.titleEn : card.title;
                        const desc = language === 'ar' ? card.descriptionAr : language === 'en' ? card.descriptionEn : card.descriptionFr;
                        const cat = language === 'ar' ? card.categoryAr : language === 'en' ? card.categoryEn : card.category;

                        return (
                            <Grid item xs={12} sm={6} md={4} key={card.id}>
                                <Card
                                    elevation={1}
                                    sx={{
                                        height: '100%',
                                        display: 'flex',
                                        flexDirection: 'column',
                                        borderRadius: 3,
                                        border: '1px solid',
                                        borderColor: 'divider',
                                        transition: 'transform 0.2s, box-shadow 0.2s',
                                        '&:hover': {
                                            transform: 'translateY(-4px)',
                                            boxShadow: '0 8px 24px rgba(0,0,0,0.08)',
                                            borderColor: 'primary.main'
                                        }
                                    }}
                                >
                                    <CardContent sx={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
                                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1.5 }}>
                                            {card.icon}
                                            <Chip label={cat} size="small" variant="outlined" color="primary" sx={{ fontSize: '0.7rem' }} />
                                        </Box>

                                        <Typography variant="h6" fontWeight={700} sx={{ fontSize: '1.05rem', mb: 0.5 }}>
                                            {title}
                                        </Typography>

                                        {language !== 'ar' && card.subtitleAr && (
                                            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1, direction: 'rtl' }}>
                                                {card.subtitleAr}
                                            </Typography>
                                        )}

                                        <Typography variant="body2" color="text.secondary" sx={{ flex: 1, fontSize: '0.82rem', mb: 2, lineHeight: 1.5 }}>
                                            {desc}
                                        </Typography>

                                        <Divider sx={{ my: 1.5 }} />

                                        <Button
                                            variant="contained"
                                            color="primary"
                                            fullWidth
                                            endIcon={isRtl ? undefined : <OpenIcon />}
                                            startIcon={isRtl ? <OpenIcon sx={{ transform: 'rotate(180deg)' }} /> : undefined}
                                            onClick={() => navigate(card.route)}
                                        >
                                            {language === 'ar' ? 'فتح نموذج الطباعة' : language === 'en' ? 'Open Edition' : 'Ouvrir l’édition'}
                                        </Button>
                                    </CardContent>
                                </Card>
                            </Grid>
                        );
                    })}
                </Grid>
            </Box>
        </GenericPage>
    );
};

export default EditionsHubPage;
